// src/pages/salas/SalasShell.jsx
//
// Cascarón del módulo de salas de juntas (/salas). Es un "mini sistema" bajo
// el mismo dominio: comparte sesión y credenciales con CaprepaDesk pero no
// usa su sidebar. Aquí se resuelve /api/salas/me (permisos, salas, config),
// se pinta la barra superior y la pantalla de "sin acceso". Las páginas hijas
// leen todo desde useSalas().
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import api from '../../services/api';
import socket from '../../services/socket';
import { AuthContext } from '../../context/AuthContext';
import SALAS_CSS from './salasStyles';

const SalasContext = createContext(null);
export const useSalas = () => useContext(SalasContext);

const Icon = ({ children, size = 16, stroke = 1.9 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{children}</svg>
);
export const I = {
    cal:    <Icon><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Icon>,
    qr:     <Icon><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><path d="M14 14h3v3M21 14v7h-4M14 21h1" /></Icon>,
    cog:    <Icon><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></Icon>,
    out:    <Icon><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5M21 12H9" /></Icon>,
    left:   <Icon><path d="m15 18-6-6 6-6" /></Icon>,
    right:  <Icon><path d="m9 18 6-6-6-6" /></Icon>,
    plus:   <Icon><path d="M12 5v14M5 12h14" /></Icon>,
    x:      <Icon size={18}><path d="M18 6 6 18M6 6l12 12" /></Icon>,
    lock:   <Icon size={40} stroke={1.5}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></Icon>,
    clock:  <Icon size={14}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>,
    user:   <Icon size={14}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Icon>,
    room:   <Icon size={14}><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></Icon>,
    print:  <Icon><path d="M6 9V3h12v6" /><rect x="6" y="14" width="12" height="7" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /></Icon>,
    desk:   <Icon size={14}><path d="M3 12 12 3l9 9" /><path d="M5 10v10h14V10" /></Icon>,
    trash:  <Icon size={14}><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></Icon>,
    edit:   <Icon size={14}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></Icon>,
    hist:   <Icon size={14}><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /><path d="M12 7v5l3 2" /></Icon>,
};

export default function SalasShell() {
    const { token, user, logout } = useContext(AuthContext);
    const location = useLocation();
    const [me, setMe] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get('/salas/me');
            setMe(data); setError('');
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudo cargar el módulo de salas');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { if (token) reload(); }, [token, reload]);

    // Suscripción a cambios de agenda (y re-suscripción al reconectar)
    useEffect(() => {
        if (!token) return undefined;
        const join = () => socket.emit('joinSalas');
        join();
        socket.on('connect', join);
        return () => { socket.off('connect', join); socket.emit('leaveSalas'); };
    }, [token]);

    const value = useMemo(() => ({ me, reload, rooms: me?.rooms || [], settings: me?.settings, access: me?.access }), [me, reload]);

    if (!token) return <Navigate to="/salas/login" replace state={{ from: location.pathname + location.search }} />;

    const canManage = !!me?.access?.canManage;
    const isAdmin   = !!me?.access?.isModuleAdmin; // admin del Desk o administrador del módulo
    const canDesk   = user && ['admin', 'agent', 'leader', 'authorizer'].includes(user.role);

    return (
        <SalasContext.Provider value={value}>
            <style>{SALAS_CSS}</style>
            <div className="sl-root">
                <header className="sl-top">
                    <div className="sl-top-in">
                        <img src="/caprepa.svg" alt="Caprepa" className="sl-logo" />
                        <div className="sl-brand"><b>Salas de juntas</b><span>Corporativo</span></div>
                        <nav className="sl-nav">
                            <span className="sl-user" title={me?.user?.puesto || ''}>{user?.name}</span>
                            <NavLink to="/salas" end>{I.cal}<span className="lbl">Agenda</span></NavLink>
                            {canManage && <NavLink to="/salas/qr">{I.qr}<span className="lbl">QR</span></NavLink>}
                            {isAdmin && <NavLink to="/salas/admin">{I.cog}<span className="lbl">Configuración</span></NavLink>}
                            {canDesk && <NavLink to="/dashboard" title="Ir a CaprepaDesk">{I.desk}<span className="lbl">Desk</span></NavLink>}
                            <button type="button" onClick={() => { logout(); window.location.href = '/salas/login'; }} title="Cerrar sesión">{I.out}<span className="lbl">Salir</span></button>
                        </nav>
                    </div>
                </header>

                <main className="sl-main">
                    {loading && <div className="sl-center"><Spinner animation="border" /></div>}
                    {!loading && error && (
                        <div className="sl-center"><div className="sl-denied">
                            <h2>Algo falló</h2><p>{error}</p>
                            <button className="sl-btn sl-btn-primary" onClick={reload}>Reintentar</button>
                        </div></div>
                    )}
                    {!loading && !error && me && !me.access.canBook && <Denied me={me} />}
                    {!loading && !error && me && me.access.canBook && <Outlet />}
                </main>
            </div>
        </SalasContext.Provider>
    );
}

function Denied({ me }) {
    const { user: u, access } = me;
    return (
        <div className="sl-center">
            <div className="sl-denied">
                <div style={{ color: 'var(--c-text-4)' }}>{I.lock}</div>
                <h2>Sin acceso a las salas</h2>
                <p>{access.reason || 'Tu usuario no tiene permiso para reservar salas de juntas.'}</p>
                <div className="who">
                    <div><span>Usuario</span><b>{u.name}</b></div>
                    <div><span>Puesto</span><b>{u.puesto || '—'}</b></div>
                    <div><span>Unidad</span><b>{u.unidad || '—'}</b></div>
                    <div><span>Sucursal</span><b>{u.sucursal || '—'}</b></div>
                </div>
                <p>Si crees que deberías tener acceso, pide a recepción que reserve por ti o a TI que revise tu perfil.</p>
            </div>
        </div>
    );
}
